// Copyright 2025, University of Maryland, All Rights Reserved
import axios from "axios";
import { CASConfig, CASUser } from "../types";
import { storage } from "./storage";
import { validateToken, extractUserFromToken } from "./token";

/**
 * Internal config type with all optional fields resolved
 */
type ResolvedCASConfig = {
  casLoginUrl: string;
  serviceUrl: string;
  apiGatewayUrl: string;
  storageKey: string;
  debug: boolean;
  maxRedirects: number;
  onUserFetch?: (token: string, user: CASUser) => Promise<CASUser>;
  onUserNotFound?: () => void;
};

/**
 * CAS Authentication Manager
 * Handles the CAS authentication flow and JWT token management
 */
export class CASAuthManager {
  private config: ResolvedCASConfig;

  constructor(config: CASConfig) {
    this.config = {
      ...config,
      storageKey: config.storageKey || "cas_jwt",
      debug: config.debug || false,
      maxRedirects: config.maxRedirects || 3,
    };

    this.log("CAS Auth Manager initialized", this.config);
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log("[CAS Auth]", ...args);
    }
  }

  private error(...args: unknown[]): void {
    console.error("[CAS Auth]", ...args);
  }

  private buildLogoutUrl(): string {
    try {
      const url = new URL(this.config.casLoginUrl);
      url.pathname = url.pathname.replace(/\/login\/?$/, "/logout");
      url.search = "";
      url.hash = "";
      return url.toString();
    } catch (error) {
      this.error("Failed to build CAS logout URL from config, using fallback:", error);
      return "https://login.umd.edu/cas/logout";
    }
  }

  /**
   * Check if user has a valid session
   */
  hasValidSession(): boolean {
    const token = storage.getToken(this.config.storageKey);
    if (!token) {
      this.log("No token found in storage");
      return false;
    }

    const validation = validateToken(token);
    if (!validation.valid) {
      this.log("Token validation failed:", validation.error);
      return false;
    }

    this.log("Valid session found");

    // Reset redirect count when we find a valid session
    // This prevents the redirect loop error when user returns with valid session
    storage.resetRedirectCount();

    return true;
  }

  /**
   * Get the current user from storage
   */
  getCurrentUser(): CASUser | null {
    const token = storage.getToken(this.config.storageKey);
    if (!token) {
      return null;
    }

    const validation = validateToken(token);
    if (!validation.valid || !validation.user) {
      return null;
    }

    return validation.user;
  }

  /**
   * Check if URL contains a CAS ticket parameter
   */
  hasTicketInURL(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has("ticket");
  }

  /**
   * Extract CAS ticket from URL
   */
  getTicketFromURL(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get("ticket");
  }

  /**
   * Redirect to CAS login page with infinite loop protection
   */
  redirectToLogin(): boolean {
    // Check redirect count to prevent infinite loops
    const redirectCount = storage.getRedirectCount();

    if (redirectCount >= this.config.maxRedirects) {
      this.error(
        `Already redirected to CAS ${this.config.maxRedirects} times. Cannot redirect again.`,
        "Please refresh the page to try again or contact support."
      );
      // Clear everything including redirect count so user can try again after refresh
      storage.clearAll(this.config.storageKey);
      return false;
    }

    this.log("Redirecting to CAS login");

    // Increment redirect counter
    storage.incrementRedirectCount();

    // Save current path for post-login redirect
    const currentPath = window.location.pathname + window.location.hash;
    storage.setRedirectPath(currentPath);

    // Build CAS login URL with service parameter
    const loginUrl = `${this.config.casLoginUrl}?service=${encodeURIComponent(this.config.serviceUrl)}`;

    this.log("Login URL:", loginUrl);
    window.location.href = loginUrl;
    return true;
  }

  /**
   * Exchange CAS ticket for JWT token
   */
  async exchangeTicketForToken(ticket: string): Promise<CASUser> {
    this.log("Exchanging ticket for JWT token");

    try {
      const response = await axios.get(this.config.apiGatewayUrl, {
        params: {
          serviceUrl: this.config.serviceUrl,
          ticket: ticket,
        },
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.data || !response.data.token) {
        throw new Error("Invalid response from authentication server");
      }

      const token = response.data.token;
      this.log("Received JWT token from backend");

      // Reset redirect count on successful authentication
      storage.resetRedirectCount();

      // Store token
      storage.setToken(token, this.config.storageKey);

      // Extract and store user info
      let user = extractUserFromToken(token);
      if (!user) {
        throw new Error("Failed to extract user from token");
      }

      // If user fetch callback is provided, call it to get additional user data
      if (this.config.onUserFetch) {
        try {
          this.log("Fetching additional user data via callback");
          user = await this.config.onUserFetch(token, user);
          this.log("User data fetched successfully");
        } catch (fetchError) {
          // Check if it's a 404 error (user not found in system)
          if (axios.isAxiosError(fetchError) && fetchError.response?.status === 404) {
            this.error("User not found in system (404)");
            storage.clearAll(this.config.storageKey);

            // Call the user not found callback if provided
            if (this.config.onUserNotFound) {
              this.config.onUserNotFound();
            }

            throw new Error("User not found in system");
          }

          // Re-throw other errors
          this.error("Failed to fetch user data:", fetchError);
          storage.clearAll(this.config.storageKey);
          throw fetchError;
        }
      }

      storage.setUser(user);
      this.log("User authenticated:", user.username);

      return user;
    } catch (error) {
      this.error("Token exchange failed:", error);
      throw error;
    }
  }

  /**
   * Clean up URL after CAS redirect (remove ticket parameter)
   */
  cleanupURL(): void {
    // Remove ticket from URL search params
    const ticket = this.getTicketFromURL();
    if (ticket) {
      const newUrl =
        window.location.pathname +
        window.location.search.replace(`ticket=${ticket}`, "").replace(/^&/, "?").replace(/\?&/, "?").replace(/\?$/, "");
      window.history.replaceState({}, document.title, newUrl);
    }

    // Get redirect path if it was stored
    const redirectPath = storage.getAndClearRedirectPath();

    if (redirectPath) {
      this.log("Restoring original path:", redirectPath);
      window.history.replaceState({}, document.title, redirectPath);
    }
  }

  /**
   * Logout user and clear session
   */
  logout(): void {
    this.log("Logging out user");

    // Clear local storage (this also resets redirect count)
    storage.clearAll(this.config.storageKey);

    // Redirect to CAS logout
    const casLogoutUrl = this.buildLogoutUrl();
    window.location.href = casLogoutUrl;
  }

  /**
   * Get the stored JWT token
   */
  getToken(): string | null {
    return storage.getToken(this.config.storageKey);
  }

  /**
   * Add Authorization header to axios request config
   */
  addAuthHeader(config: Record<string, unknown>): Record<string, unknown> {
    const token = this.getToken();
    if (token) {
      return {
        ...config,
        headers: {
          ...(config.headers as Record<string, string> || {}),
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return config;
  }

  /**
   * Refresh the JWT token by performing a silent redirect to CAS
   * If the CAS session (TGT) is still valid, this will be transparent to the user
   * If the CAS session expired, user will be prompted to log in again
   */
  async refreshToken(): Promise<CASUser> {
    this.log("Refreshing JWT token via silent CAS redirect");

    // Store a flag to indicate we're doing a silent refresh
    storage.setItem("cas_silent_refresh", "true");

    // Save current path
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    storage.setRedirectPath(currentPath);

    // Build CAS login URL - if CAS session is valid, this will redirect back immediately
    const loginUrl = `${this.config.casLoginUrl}?service=${encodeURIComponent(this.config.serviceUrl)}`;

    this.log("Redirecting to CAS for silent refresh:", loginUrl);

    // Redirect to CAS - if session is valid, user won't see login page
    window.location.href = loginUrl;

    // This will never resolve because we're redirecting
    // The promise is here for type consistency
    return new Promise(() => {});
  }
}
